/** Holdings table columns — shared by Holdings and ARK ETF tabs. */

export const STOCK_PCT_FIELDS = new Set([
  "pct_change",
  "pct_change_5d",
  "pct_10d_ma",
  "pct_50d_ma",
  "pct_change_1m",
  "pct_change_3m",
  "pct_change_52w",
  "pct_52w_high",
  "pct_52w_low",
  "short_float",
]);

export const STOCK_NUMERIC_FIELDS = [
  "pct_change",
  "pct_change_5d",
  "pct_10d_ma",
  "pct_50d_ma",
  "pct_change_1m",
  "pct_change_3m",
  "pct_change_52w",
  "pct_52w_high",
  "pct_52w_low",
  "price_sales",
  "market_cap",
  "short_float",
  "pe_fwd",
  "pe_ttm",
  "equity",
];

export const STOCK_TABLE_COLUMNS = [
  { id: "symbol", label: "Symbol" },
  { id: "name", label: "Name" },
  { id: "earnings_date", label: "Earnings Date" },
  { id: "pct_change", label: "%Change" },
  { id: "pct_change_5d", label: "5D %Chg" },
  { id: "pct_10d_ma", label: "% 10D MA" },
  { id: "pct_50d_ma", label: "% 50D MA" },
  { id: "pct_change_1m", label: "1M %Chg" },
  { id: "pct_change_3m", label: "3M %Chg" },
  { id: "pct_change_52w", label: "52W %Chg" },
  { id: "pct_52w_high", label: "52W %/High" },
  { id: "pct_52w_low", label: "52W %/Low" },
  { id: "price_sales", label: "Price/Sales" },
  { id: "market_cap", label: "Market Cap, $B" },
  { id: "analyst_rating", label: "Analyst Rating" },
  { id: "short_float", label: "Short Float" },
  { id: "pe_fwd", label: "P/E fwd" },
  { id: "pe_ttm", label: "P/E ttm" },
  { id: "sector", label: "Sector" },
  { id: "industry", label: "Industry" },
  { id: "equity", label: "Equity" },
];

export const STOCK_TABLE_DEFAULT_VISIBLE = [
  "symbol",
  "name",
  "earnings_date",
  "pct_change",
  "pct_change_5d",
  "pct_change_1m",
  "pct_change_52w",
  "market_cap",
  "analyst_rating",
  "sector",
  "industry",
];

export const STOCK_TABLE_KEY = "stock.table";
export const STOCK_ARK_BUY_TABLE_KEY = "stock.ark.table";

/** ARK ETF tab columns (inserted before Earnings Date). */
export const STOCK_ARK_TRADE_DATE_COLUMN = { id: "trade_date", label: "Trade Date" };
export const STOCK_ARK_MARKET_VALUE_COLUMN = { id: "ark_market_value", label: "ARK Market Value" };
export const STOCK_ARK_WEIGHT_COLUMN = { id: "ark_weight", label: "Weight %" };

const STOCK_ARK_EXTRA_COLUMN_IDS = new Set([
  STOCK_ARK_TRADE_DATE_COLUMN.id,
  STOCK_ARK_MARKET_VALUE_COLUMN.id,
  STOCK_ARK_WEIGHT_COLUMN.id,
]);

export const STOCK_TABLE_SORT_KEY_TYPES = {
  symbol: "string",
  name: "string",
  trade_date: "date",
  ark_market_value: "number",
  ark_weight: "number",
  earnings_date: "date",
  analyst_rating: "string",
  sector: "string",
  industry: "string",
  ...Object.fromEntries(STOCK_NUMERIC_FIELDS.map((f) => [f, "number"])),
};

const STOCK_SEARCH_FIELDS = ["symbol", "name", "sector", "industry", "analyst_rating"];

/**
 * @param {object} row
 * @param {string} searchTerm
 */
export function stockHoldingsRowMatchesSearch(row, searchTerm) {
  const searchLower = (searchTerm || "").toLowerCase().trim();
  if (!searchLower) return true;
  const hay = STOCK_SEARCH_FIELDS.map((key) => row[key])
    .map((v) => (v != null ? String(v).toLowerCase() : ""))
    .join(" ");
  return hay.includes(searchLower);
}

/**
 * @param {string[]} visibleColumnIds
 */
export function resolveStockTableVisibleColumns(visibleColumnIds) {
  const allowed = new Set(STOCK_TABLE_COLUMNS.map((c) => c.id));
  const ids = Array.isArray(visibleColumnIds) ? visibleColumnIds.filter((id) => allowed.has(id)) : [];
  if (ids.length > 0) return ids;
  return [...STOCK_TABLE_DEFAULT_VISIBLE];
}

/**
 * Holdings visible columns plus ARK trade date before earnings date (ARK ETF tab).
 * @param {string[]} visibleColumnIds
 */
export function buildArkBuyDisplayColumnIds(visibleColumnIds) {
  const base = resolveStockTableVisibleColumns(visibleColumnIds);
  const arkCols = [
    STOCK_ARK_TRADE_DATE_COLUMN.id,
    STOCK_ARK_MARKET_VALUE_COLUMN.id,
    STOCK_ARK_WEIGHT_COLUMN.id,
  ];
  const withoutDupes = base.filter((id) => !STOCK_ARK_EXTRA_COLUMN_IDS.has(id));
  const earningsIdx = withoutDupes.indexOf("earnings_date");
  if (earningsIdx >= 0) {
    return [
      ...withoutDupes.slice(0, earningsIdx),
      ...arkCols,
      ...withoutDupes.slice(earningsIdx),
    ];
  }
  const nameIdx = withoutDupes.indexOf("name");
  const insertAt = nameIdx >= 0 ? nameIdx + 1 : 1;
  return [...withoutDupes.slice(0, insertAt), ...arkCols, ...withoutDupes.slice(insertAt)];
}

/**
 * @param {string[]} columnIds
 */
export function stockHoldingsColumnsFromIds(columnIds) {
  const byId = new Map(STOCK_TABLE_COLUMNS.map((c) => [c.id, c]));
  byId.set(STOCK_ARK_TRADE_DATE_COLUMN.id, STOCK_ARK_TRADE_DATE_COLUMN);
  byId.set(STOCK_ARK_MARKET_VALUE_COLUMN.id, STOCK_ARK_MARKET_VALUE_COLUMN);
  byId.set(STOCK_ARK_WEIGHT_COLUMN.id, STOCK_ARK_WEIGHT_COLUMN);
  return (columnIds || []).map((id) => byId.get(id)).filter(Boolean);
}

/** All columns available in the ARK ETF table customizer (holdings + ARK-specific). */
export const STOCK_ARK_BUY_TABLE_COLUMNS = stockHoldingsColumnsFromIds(
  buildArkBuyDisplayColumnIds(STOCK_TABLE_COLUMNS.map((c) => c.id))
);

export const STOCK_ARK_BUY_TABLE_DEFAULT_VISIBLE = buildArkBuyDisplayColumnIds(
  STOCK_TABLE_DEFAULT_VISIBLE
);

/**
 * @param {string[]} visibleColumnIds
 */
export function resolveArkBuyVisibleColumns(visibleColumnIds) {
  const allowed = new Set(STOCK_ARK_BUY_TABLE_COLUMNS.map((c) => c.id));
  const ids = Array.isArray(visibleColumnIds) ? visibleColumnIds.filter((id) => allowed.has(id)) : [];
  if (ids.length > 0) return ids;
  return [...STOCK_ARK_BUY_TABLE_DEFAULT_VISIBLE];
}

/**
 * Parse ARK weight cell for sorting (e.g. "9.51%").
 * @param {unknown} raw
 */
export function parseArkWeightSortValue(raw) {
  if (raw == null || raw === "") return Number.NEGATIVE_INFINITY;
  const cleaned = String(raw).replace(/%/g, "").replace(/,/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

export function formatStockNumber(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** Display market cap in billions ($B). */
export function formatStockMarketCapBillions(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function formatStockCurrency(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}
